/**
 * Unified Beneficiary Service
 * 
 * This service handles all beneficiary-related API calls using the new
 * unified beneficiary architecture (one person = one beneficiary, multiple accounts).
 */

import { APP_CONFIG } from '../config/app-config';
import { getToken } from '../utils/authToken';
import { isLikelySaMobileMsisdn } from '../utils/saBankAccountValidation';

const API_BASE = APP_CONFIG.API.baseUrl;

export type PaymentRail = 'mymoolah' | 'eft' | 'payshap' | 'moolahmove' | 'mobile_money' | 'international_bank' | 'unspecified';

export interface BeneficiaryAccount {
  id: number;
  type: 'mymoolah' | 'bank' | 'mobile_money' | 'international_bank' | 'airtime' | 'data' | 'electricity' | 'biller' | 'voucher';
  /** The payment network used to send money via this account. */
  paymentRail?: PaymentRail;
  identifier: string; // account number, MSISDN, meter number, etc.
  walletMsisdn?: string;
  /** Contact mobile number linked to a bank account (used for PayShap RTP / FICA). */
  mobileNumber?: string;
  bankName?: string;
  accountNumber?: string;
  accountType?: string;
  branchCode?: string;
  /** SWIFT/BIC code for international bank transfers (MoolahMove) */
  swiftBic?: string;
  /** IBAN for international bank transfers (MoolahMove) */
  iban?: string;
  /** ISO 3166-1 alpha-2 country code for international methods (MoolahMove) */
  countryCode?: string;
  provider?: string;
  mobileMoneyId?: string;
  label?: string;
  isDefault: boolean;
  isActive?: boolean;
  metadata?: {
    bankName?: string;
    accountType?: string;
    branchCode?: string;
    network?: string;
    meterType?: string;
    provider?: string;
    billerName?: string;
    paymentRail?: PaymentRail;
    [key: string]: any;
  };
}

export interface UnifiedBeneficiary {
  id: number;
  name: string;
  msisdn?: string;
  identifier?: string;
  accountType?: string;
  bankName?: string;
  accounts: BeneficiaryAccount[];
  isFavorite: boolean;
  notes?: string;
  preferredPaymentMethod?: string;
  lastPaidAt?: string;
  timesPaid: number;
  createdAt: string;
  updatedAt: string;
}

export interface PaymentBeneficiary {
  id: string;
  name: string;
  msisdn?: string;
  identifier: string;
  accountType: 'mymoolah' | 'bank';
  bankName?: string;
  userId?: string | number;
  createdAt?: string;
  updatedAt?: string;
  timesPaid?: number;
  lastPaidAt?: string;
  lastPaid?: Date;
    isFavorite?: boolean;
    totalPaid?: number;
    paymentCount?: number;
  metadata?: Record<string, any>;
  accounts?: BeneficiaryAccount[];
}

export interface CreateBeneficiaryRequest {
  name: string;
  msisdn?: string;
  serviceType:
    | 'mymoolah'
    | 'bank'
    | 'eft'
    | 'payshap'
    | 'moolahmove'
    | 'international_bank'
    | 'mobile_money'
    | 'airtime'
    | 'data'
    | 'electricity'
    | 'biller'
    | 'voucher';
  serviceData: {
    walletMsisdn?: string;
    bankName?: string;
    accountNumber?: string;
    accountType?: string;
    branchCode?: string;
    /** SWIFT/BIC code for international bank transfers (MoolahMove) */
    swiftBic?: string;
    /** IBAN for international bank transfers (MoolahMove) */
    iban?: string;
    /** ISO 3166-1 alpha-2 country code for international methods (MoolahMove) */
    countryCode?: string;
    provider?: string;
    mobileMoneyId?: string;
    msisdn?: string;
    network?: string;
    meterNumber?: string;
    meterType?: string;
    billerName?: string;
    label?: string;
    isDefault?: boolean;
    /** Explicit payment rail override (optional — inferred from serviceType if omitted) */
    paymentRail?: PaymentRail;
    [key: string]: any;
  };
  isFavorite?: boolean;
  notes?: string;
  /** Which list this save targets: 'beneficiary' (Send Money) or 'payer' (Request Money). */
  role?: 'beneficiary' | 'payer';
}

export interface AddServiceRequest {
  serviceType: string;
  serviceData: Record<string, any>;
}

/**
 * True when a value is shaped like a South African mobile number
 * (0XXXXXXXXX, 27XXXXXXXXX, or +27XXXXXXXXX with a 6/7/8 prefix).
 *
 * Used to make sure we never treat a bank account number as a phone number
 * when prefilling Send Money / Request Money forms.
 */
export function isLikelySaMobile(value?: string | null): boolean {
  return isLikelySaMobileMsisdn(value);
}

/**
 * Resolve the real contact mobile number for a payment contact, optionally
 * scoped to a specific selected account.
 *
 * Banking-grade rule: for BANK contacts the "primary identifier" is the bank
 * account number, which must NEVER be surfaced as the contact's phone. The
 * mobile for a bank contact lives on the bank account (`mobileNumber`) or on
 * the contact's MyMoolah wallet account. This helper enforces that ordering
 * and only ever returns a value that is shaped like an SA mobile number.
 */
export function resolveContactMobile(
  contact: { msisdn?: string; identifier?: string; accounts?: BeneficiaryAccount[] },
  account?: BeneficiaryAccount | null
): string {
  // 1) Account-scoped resolution when a specific account is selected.
  if (account) {
    if (account.type === 'bank') {
      const m = account.mobileNumber || account.metadata?.mobileNumber;
      if (isLikelySaMobile(m)) return m as string;
    } else if (account.type === 'mymoolah') {
      const w = account.identifier || account.walletMsisdn || account.metadata?.walletMsisdn;
      if (isLikelySaMobile(w)) return w as string;
    }
  }

  // 2) Any bank account on the contact that carries a mobile (PayShap / FICA).
  const bankWithMobile = (contact.accounts || []).find(
    (a) => a.type === 'bank' && isLikelySaMobile(a.mobileNumber || a.metadata?.mobileNumber)
  );
  if (bankWithMobile) {
    return (bankWithMobile.mobileNumber || bankWithMobile.metadata?.mobileNumber) as string;
  }

  // 3) The contact-level msisdn, but ONLY if it is actually a phone number.
  if (isLikelySaMobile(contact.msisdn)) return contact.msisdn as string;

  // 4) A MyMoolah wallet account identifier (which is the wallet phone).
  const wallet = (contact.accounts || []).find((a) => a.type === 'mymoolah');
  if (wallet && isLikelySaMobile(wallet.identifier)) return wallet.identifier;

  return '';
}

class BeneficiaryService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<{ success: boolean; data?: T; message?: string; error?: string }> {
    try {
      const url = `${API_BASE}${endpoint}`;
      let token = getToken();
      
      // Filter out demo tokens - they should not be sent to the backend
      if (token && token.startsWith('demo-token-')) {
        token = null;
      }
      
      // Handle missing token gracefully (same as apiClient.ts)
      // Let the backend return 401/403 if token is missing
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      };

      const response = await fetch(url, config);

      // Some upstream errors (e.g. Cloud Run 502/504) return HTML or plain text,
      // not JSON. If we always call response.json() we get a SyntaxError like:
      // "Unexpected token 'u', \"upstream r\"... is not valid JSON".
      // Detect content type first and fall back to text so we can surface a
      // useful error message instead of a parse failure.
      const contentType = response.headers.get('content-type') || '';
      let data: any = {};

      if (contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch {
          // If JSON parsing fails, keep data as empty object and handle below
          data = {};
        }
      } else {
        // Non-JSON response (likely an HTML or plain text error page)
        const text = await response.text().catch(() => '');
        data = text ? { message: text } : {};
      }

      if (!response.ok) {
        // Handle 401 Unauthorized - token might be expired or missing
        if (response.status === 401 || response.status === 403) {
          throw new Error(data.message || data.error || 'Session expired. Please log in again.');
        }
        throw new Error(data.message || data.error || `HTTP ${response.status}`);
      }

      return {
        success: true,
        data: data.data || data
      };
    } catch (error) {
      console.error(`Beneficiary API Error (${endpoint}):`, error);
      throw error;
    }
  }

  /**
   * Get beneficiaries filtered by service type
   * @param serviceType - 'payment', 'airtime-data', 'electricity', 'biller', 'voucher'
   * @param search - Optional search query
   */
  async getBeneficiariesByService(
    serviceType: 'payment' | 'airtime-data' | 'electricity' | 'biller' | 'voucher',
    search: string = '',
    role?: 'beneficiary' | 'payer'
  ): Promise<UnifiedBeneficiary[]> {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (role) params.append('role', role);
    const qs = params.toString();
    const response = await this.request<{ beneficiaries: any[] }>(
      `/api/v1/unified-beneficiaries/by-service/${serviceType}${qs ? `?${qs}` : ''}`
    );
    
    // Transform legacy format to unified format
    return (response.data?.beneficiaries || []).map(this.transformLegacyBeneficiary);
  }

  /**
   * Get payment beneficiaries (MyMoolah + Bank).
   * Pass role to scope to a single list: 'beneficiary' (Send Money) or 'payer' (Request Money).
   */
  async getPaymentBeneficiaries(
    search: string = '',
    role?: 'beneficiary' | 'payer'
  ): Promise<PaymentBeneficiary[]> {
    const unified = await this.getBeneficiariesByService('payment', search, role);
    return unified.map((beneficiary, idx) => this.mapToPaymentBeneficiary(beneficiary, idx));
  }

  /**
   * Get Send-Money beneficiaries (payment contacts flagged as beneficiaries).
   */
  async getSendBeneficiaries(search: string = ''): Promise<PaymentBeneficiary[]> {
    return this.getPaymentBeneficiaries(search, 'beneficiary');
  }

  /**
   * Get Request-Money payers (payment contacts flagged as payers).
   */
  async getPayerBeneficiaries(search: string = ''): Promise<PaymentBeneficiary[]> {
    return this.getPaymentBeneficiaries(search, 'payer');
  }

  /**
   * Get electricity beneficiaries
   */
  async getElectricityBeneficiaries(search: string = ''): Promise<UnifiedBeneficiary[]> {
    return await this.getBeneficiariesByService('electricity', search);
  }

  /**
   * Get airtime/data beneficiaries
   */
  async getAirtimeDataBeneficiaries(search: string = ''): Promise<UnifiedBeneficiary[]> {
    const list = await this.getBeneficiariesByService('airtime-data', search);
    // Filter to beneficiaries that actually have airtime or data accounts (avoid stale legacy rows)
    return list.filter((b) =>
      (b.accounts || []).some((a) => a.type === 'airtime' || a.type === 'data')
    );
  }

  /**
   * Get bill payment beneficiaries
   */
  async getBillPaymentBeneficiaries(search: string = ''): Promise<UnifiedBeneficiary[]> {
    return await this.getBeneficiariesByService('biller', search);
  }

  /**
   * Get all beneficiaries (legacy method for overlay service)
   */
  async getAllBeneficiaries(type?: string, search: string = ''): Promise<UnifiedBeneficiary[]> {
    if (type) {
      return await this.getBeneficiariesByService(type as any, search);
    }
    // If no type specified, return all (not recommended, but for backward compatibility)
    const response = await this.request<{ beneficiaries: any[] }>(
      `/api/v1/unified-beneficiaries/search?q=${encodeURIComponent(search)}`
    );
    return (response.data?.beneficiaries || []).map(this.transformLegacyBeneficiary);
  }

  /**
   * Save beneficiary (legacy method for overlay service)
   */
  async saveBeneficiary(data: CreateBeneficiaryRequest): Promise<UnifiedBeneficiary> {
    return await this.createOrUpdateBeneficiary(data);
  }

  /**
   * Remove all services of specific types from a beneficiary
   * Banking-grade: Only removes service accounts, never affects beneficiary record or user account
   * Use this when removing from service-specific pages (e.g., airtime/data page removes all airtime+data)
   */
  async removeAllServicesOfType(
    beneficiaryId: number | string,
    serviceType: 'airtime-data' | 'electricity' | 'biller' | string
  ): Promise<void> {
    await this.request(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services/${serviceType}`,
      {
        method: 'DELETE'
      }
    );
    // Optionally return void; callers can refresh state afterward
  }

  /**
   * Remove beneficiary from a specific service context
   * Banking-grade: Only removes service accounts, never deletes beneficiary record
   * Supports both number and string IDs for backward compatibility
   */
  async removeBeneficiary(
    beneficiaryId: number | string,
    context?: 'airtime-data' | 'electricity' | 'biller' | 'payment'
  ): Promise<void> {
    // If context is provided, remove all services of that type
    if (context) {
      await this.removeAllServicesOfType(beneficiaryId, context);
      return;
    }

    // Legacy behavior: Return success (placeholder for backward compatibility)
    // New code should explicitly provide context
    console.warn('removeBeneficiary called without context - this is deprecated. Use removeAllServicesOfType instead.');
    return Promise.resolve();
  }

  /**
   * Delete beneficiary by removing all of its service accounts.
   * Banking-grade: the beneficiary record itself is preserved (audit trail).
   */
  async deleteBeneficiary(beneficiaryId: number | string): Promise<void> {
    await this.request(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services/biller`,
      { method: 'DELETE' }
    );
  }

  /**
   * Create or update a payment beneficiary (MyMoolah wallet or Bank account)
   */
  async createPaymentBeneficiary(options: {
    name: string;
    msisdn?: string; // Optional for bank accounts
    accountType: 'mymoolah' | 'bank';
    bankName?: string;
    accountNumber?: string;
    branchCode?: string;
    /** Contact mobile number to persist alongside a bank account (PayShap RTP / FICA). */
    mobile?: string;
    /** Which list this contact belongs to: 'beneficiary' (Send Money) or 'payer' (Request Money). */
    role?: 'beneficiary' | 'payer';
  }): Promise<PaymentBeneficiary> {
    const serviceType = options.accountType === 'bank' ? 'bank' : 'mymoolah';

    // For MyMoolah, msisdn is required. For bank accounts, we don't use MSISDN (backend generates NON_MSI_ identifier)
    let normalizedMsisdn: string | undefined;
    if (serviceType === 'mymoolah') {
      if (!options.msisdn) {
        throw new Error('MSISDN is required for MyMoolah accounts');
      }
      normalizedMsisdn = this.normalizeMsisdn(options.msisdn);
    } else {
      // For bank accounts, we don't pass MSISDN - backend will generate NON_MSI_ identifier
      // PayShap reference is stored separately in serviceData
      normalizedMsisdn = undefined;
    }

    // Bank accounts keep the contact's mobile in serviceData so it round-trips
    // (mergeServiceData stores bank serviceData verbatim in paymentMethods.bankAccounts[]).
    const bankMobile = options.mobile ? this.normalizeMsisdn(options.mobile) : undefined;

    const serviceData =
      serviceType === 'bank'
        ? {
            bankName: options.bankName,
            accountNumber: options.accountNumber,
            accountType: 'cheque',
            branchCode: options.branchCode,
            mobileNumber: bankMobile,
            isDefault: true
          }
        : {
            walletMsisdn: normalizedMsisdn,
            isDefault: true
          };

    const created = await this.createOrUpdateBeneficiary({
      name: options.name,
      msisdn: normalizedMsisdn, // undefined for bank accounts (backend will use NON_MSI_ identifier)
      serviceType,
      serviceData,
      ...(options.role ? { role: options.role } : {})
    });

    return this.mapToPaymentBeneficiary(created);
  }

  /**
   * Create or update a beneficiary
   */
  async createOrUpdateBeneficiary(request: CreateBeneficiaryRequest): Promise<UnifiedBeneficiary> {
    const response = await this.request<any>(
      '/api/v1/unified-beneficiaries',
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
    
    return this.transformLegacyBeneficiary(response.data);
  }

  /**
   * Add a service/account to an existing beneficiary
   */
  async addServiceToBeneficiary(
    beneficiaryId: number,
    request: AddServiceRequest
  ): Promise<void> {
    await this.request(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services`,
      {
        method: 'POST',
        body: JSON.stringify(request)
      }
    );
  }

  /**
   * Remove a service/account from a beneficiary
   */
  async removeServiceFromBeneficiary(
    beneficiaryId: number,
    serviceType: string,
    serviceId: string
  ): Promise<void> {
    await this.request(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services/${serviceType}/${serviceId}`,
      {
        method: 'DELETE'
      }
    );
  }

  /**
   * Remove a specific payment account (by account id) from a beneficiary.
   * Maps to DELETE /api/v1/unified-beneficiaries/:id/services/:type/:accountId
   */
  async removePaymentAccount(
    beneficiaryId: number | string,
    accountType: 'mymoolah' | 'bank',
    accountId: number | string
  ): Promise<void> {
    await this.request(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services/${accountType}/${accountId}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Get all services/accounts for a specific beneficiary
   */
  async getBeneficiaryServices(beneficiaryId: number): Promise<UnifiedBeneficiary> {
    const response = await this.request<any>(
      `/api/v1/unified-beneficiaries/${beneficiaryId}/services`
    );
    
    return this.transformLegacyBeneficiary(response.data);
  }

  /**
   * Update beneficiary metadata (favorite, notes, preferred method) or service accounts
   * Supports both number and string IDs for backward compatibility
   */
  async updateBeneficiary(
    beneficiaryId: number | string,
    updates: {
      isFavorite?: boolean;
      notes?: string;
      preferredPaymentMethod?: string;
      name?: string;
      identifier?: string;
      bankName?: string;
      metadata?: any;
      serviceType?: string;
      serviceData?: any;
    }
  ): Promise<UnifiedBeneficiary> {
    const id = typeof beneficiaryId === 'string' ? parseInt(beneficiaryId, 10) : beneficiaryId;
    const response = await this.request<any>(
      `/api/v1/unified-beneficiaries/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(updates)
      }
    );
    
    return this.transformLegacyBeneficiary(response.data);
  }

  /**
   * Set or clear a contact's list-membership role flags.
   *
   * Use this to remove a contact from a single list (e.g. { isPayer: false } to
   * take it out of the Payers list) without deleting the contact or affecting
   * the other list. Maps to PATCH /api/v1/unified-beneficiaries/:id.
   */
  async setBeneficiaryRole(
    beneficiaryId: number | string,
    roles: { isPayer?: boolean; isBeneficiary?: boolean }
  ): Promise<UnifiedBeneficiary> {
    const id = typeof beneficiaryId === 'string' ? parseInt(beneficiaryId, 10) : beneficiaryId;
    const response = await this.request<any>(
      `/api/v1/unified-beneficiaries/${id}`,
      {
        method: 'PATCH',
        body: JSON.stringify(roles)
      }
    );

    return this.transformLegacyBeneficiary(response.data);
  }

  /**
   * Search beneficiaries across all services
   */
  async searchBeneficiaries(
    query: string,
    serviceType?: string
  ): Promise<UnifiedBeneficiary[]> {
    const params = new URLSearchParams({ q: query });
    if (serviceType) params.append('serviceType', serviceType);
    
    const response = await this.request<{ beneficiaries: any[] }>(
      `/api/v1/unified-beneficiaries/search?${params.toString()}`
    );
    
    return (response.data?.beneficiaries || []).map(this.transformLegacyBeneficiary);
  }

  /**
   * Transform legacy beneficiary format to unified format
   * This bridges the gap between old JSONB structure and new normalized tables
   */
  private transformLegacyBeneficiary(legacy: any): UnifiedBeneficiary {
    const accounts: BeneficiaryAccount[] = [];

    // The contact's bank mobile (PayShap RTP / FICA) is only stored in the JSONB
    // paymentMethods.bankAccounts[].mobileNumber — it has no dedicated column on
    // the normalized beneficiary_payment_methods table. Build a lookup so we can
    // re-attach it when the backend returns the normalized accounts[] array
    // (otherwise the stored mobile would be silently lost on read-back).
    const bankMobileByAccount = new Map<string, string>();
    const jsonbBankAccounts: any[] = Array.isArray(legacy?.paymentMethods?.bankAccounts)
      ? legacy.paymentMethods.bankAccounts
      : [];
    jsonbBankAccounts.forEach((ba) => {
      if (ba && ba.accountNumber && ba.mobileNumber) {
        bankMobileByAccount.set(String(ba.accountNumber), String(ba.mobileNumber));
      }
    });
    
    // New unified path: if backend already sent a normalized accounts[]
    // array (including airtime/data service accounts from BeneficiaryServiceAccount),
    // use it directly as the source of truth. This preserves metadata.network
    // for PINless filtering and avoids double-transforming.
    // Banking-grade: Filter out inactive accounts and deduplicate airtime/data accounts
    if (Array.isArray(legacy.accounts) && legacy.accounts.length > 0) {
      const accountMap = new Map<string, any>();
      
      legacy.accounts.forEach((acc: any) => {
        // Skip inactive accounts - backend should filter, but add safety check
        if (acc.isActive === false || acc.metadata?.isActive === false) {
          return;
        }
        
        const identifier = acc.identifier?.trim() || '';
        const isAirtimeOrData = acc.type === 'airtime' || acc.type === 'data';
        
        if (isAirtimeOrData && identifier) {
          // OPTION 1: Only 'airtime' service accounts are created now, but keep deduplication
          // for backward compatibility with existing beneficiaries that may have both airtime and data
          const existing = accountMap.get(identifier);
          if (existing) {
            // Merge: Keep the account with more metadata or prefer airtime
            // This handles legacy data where both airtime and data service accounts exist
            if (acc.type === 'airtime' || (!existing.metadata?.network && acc.metadata?.network)) {
              accountMap.set(identifier, {
                id: acc.id, // Use the first account's ID (or prefer airtime ID)
                type: acc.type, // Prefer airtime type for display
                identifier: acc.identifier,
                label: acc.label || existing.label,
                isDefault: acc.isDefault || existing.isDefault,
                metadata: {
                  ...acc.metadata,
                  ...existing.metadata,
                  // Track both service types for backward compatibility
                  _serviceTypes: [...(acc.metadata?._serviceTypes || [acc.type]), existing.type].filter((v, i, a) => a.indexOf(v) === i)
                }
              });
            } else {
              // Update existing with additional metadata
              existing.metadata = {
                ...existing.metadata,
                ...acc.metadata,
                _serviceTypes: [...(existing.metadata?._serviceTypes || [existing.type]), acc.type].filter((v, i, a) => a.indexOf(v) === i)
              };
            }
          } else {
            // First occurrence of this identifier (new beneficiaries will only have 'airtime')
            accountMap.set(identifier, {
              id: acc.id,
              type: acc.type,
              identifier: acc.identifier,
              label: acc.label,
              isDefault: !!acc.isDefault,
              metadata: {
                ...(acc.metadata || {}),
                _serviceTypes: [acc.type]
              }
            });
          }
        } else {
          // Non-airtime/data accounts or accounts without identifier - add as-is, preserving all fields
          const key = identifier || `account-${acc.id}`;
          // Re-attach the bank contact mobile from JSONB when the normalized
          // record does not carry it (the normalized table has no mobile column).
          const resolvedMobile =
            acc.mobileNumber ||
            acc.metadata?.mobileNumber ||
            (acc.type === 'bank'
              ? bankMobileByAccount.get(String(acc.accountNumber || acc.identifier || ''))
              : undefined);
          accountMap.set(key, {
            id: acc.id,
            type: acc.type,
            paymentRail: acc.paymentRail,
            identifier: acc.identifier,
            walletMsisdn: acc.walletMsisdn,
            mobileNumber: resolvedMobile,
            bankName: acc.bankName,
            accountNumber: acc.accountNumber,
            accountType: acc.accountType,
            branchCode: acc.branchCode,
            swiftBic: acc.swiftBic,
            iban: acc.iban,
            countryCode: acc.countryCode,
            provider: acc.provider,
            mobileMoneyId: acc.mobileMoneyId,
            label: acc.label,
            isDefault: !!acc.isDefault,
            isActive: acc.isActive !== false,
            metadata: {
              ...(acc.metadata || {}),
              bankName: acc.bankName || acc.metadata?.bankName,
              mobileNumber: resolvedMobile || acc.metadata?.mobileNumber,
              paymentRail: acc.paymentRail || acc.metadata?.paymentRail
            }
          });
        }
      });
      
      // Convert map to array
      accounts.push(...Array.from(accountMap.values()));
    } else {
      // Legacy JSONB path: reconstruct accounts from paymentMethods + vasServices
      if (legacy.paymentMethods) {
        // MyMoolah wallet
        if (legacy.paymentMethods.mymoolah) {
          accounts.push({
            id: legacy.id * 1000 + 1, // Generate stable ID
            type: 'mymoolah',
            identifier:
              legacy.paymentMethods.mymoolah.walletId ||
              legacy.paymentMethods.mymoolah.walletMsisdn ||
              legacy.identifier,
            label: 'MyMoolah Wallet',
            isDefault: legacy.preferredPaymentMethod === 'mymoolah',
            metadata: legacy.paymentMethods.mymoolah
          });
        }
        
        // Bank accounts
        if (legacy.paymentMethods.bankAccounts && Array.isArray(legacy.paymentMethods.bankAccounts)) {
          legacy.paymentMethods.bankAccounts.forEach((bank: any, idx: number) => {
            // Quote/submit look up beneficiary_payment_methods.id. Do not invent
            // beneficiaryId*1000+n — that 404s Instant Payment and EFT.
            if (!bank.id) {
              return;
            }
            accounts.push({
              id: bank.id,
              type: 'bank',
              paymentRail: (bank.paymentRail as PaymentRail) || 'payshap',
              identifier: bank.accountNumber,
              bankName: bank.bankName,
              accountNumber: bank.accountNumber,
              accountType: bank.accountType,
              branchCode: bank.branchCode,
              mobileNumber: bank.mobileNumber,
              label: bank.label || `${bank.bankName} ${bank.accountType}`,
              isDefault: legacy.preferredPaymentMethod === 'bank' && idx === 0,
              metadata: {
                bankName: bank.bankName,
                accountType: bank.accountType,
                branchCode: bank.branchCode,
                paymentRail: bank.paymentRail || 'payshap',
                ...bank
              }
            });
          });
        }

        // International bank accounts (MoolahMove)
        if (legacy.paymentMethods.internationalBankAccounts && Array.isArray(legacy.paymentMethods.internationalBankAccounts)) {
          legacy.paymentMethods.internationalBankAccounts.forEach((bank: any, idx: number) => {
            accounts.push({
              id: bank.id || legacy.id * 1000 + 20 + idx,
              type: 'international_bank',
              paymentRail: 'moolahmove',
              identifier: bank.accountNumber || bank.iban || '',
              bankName: bank.bankName,
              accountNumber: bank.accountNumber,
              swiftBic: bank.swiftBic,
              iban: bank.iban,
              countryCode: bank.countryCode,
              label: bank.label || `${bank.bankName || 'International'} (${bank.countryCode || ''})`,
              isDefault: bank.isDefault || false,
              metadata: { ...bank, paymentRail: 'moolahmove' }
            });
          });
        }

        // Mobile money accounts (MoolahMove international e-wallets)
        if (legacy.paymentMethods.mobileMoney && Array.isArray(legacy.paymentMethods.mobileMoney)) {
          legacy.paymentMethods.mobileMoney.forEach((mm: any, idx: number) => {
            accounts.push({
              id: mm.id || legacy.id * 1000 + 30 + idx,
              type: 'mobile_money',
              paymentRail: 'mobile_money',
              identifier: mm.mobileMoneyId || '',
              provider: mm.provider,
              mobileMoneyId: mm.mobileMoneyId,
              countryCode: mm.countryCode,
              label: mm.label || `${mm.provider || 'Mobile Money'} (${mm.countryCode || ''})`,
              isDefault: mm.isDefault || false,
              metadata: { ...mm, paymentRail: 'mobile_money' }
            });
          });
        }
      }
      
      // Extract service accounts (airtime, data, electricity, etc.)
      // Banking-grade: Deduplicate airtime/data accounts with same identifier
      if (legacy.vasServices) {
        const vasAccountMap = new Map<string, any>();
        
        if (legacy.vasServices.airtime && Array.isArray(legacy.vasServices.airtime)) {
          legacy.vasServices.airtime.forEach((service: any, idx: number) => {
            if (service.isActive === false) return;
            const identifier = (service.mobileNumber || service.msisdn || '').trim();
            if (identifier) {
              vasAccountMap.set(identifier, {
                id: legacy.id * 1000 + 100 + idx,
                type: 'airtime',
                identifier: identifier,
                label: service.label || `Airtime - ${service.network || ''}`,
                isDefault: false,
                metadata: {
                  network: service.network,
                  ...service,
                  _serviceTypes: ['airtime']
                }
              });
            }
          });
        }
        
        if (legacy.vasServices.data && Array.isArray(legacy.vasServices.data)) {
          legacy.vasServices.data.forEach((service: any, idx: number) => {
            if (service.isActive === false) return;
            const identifier = (service.mobileNumber || service.msisdn || '').trim();
            if (identifier) {
              const existing = vasAccountMap.get(identifier);
              if (existing) {
                // Merge with existing airtime account
                existing.metadata = {
                  ...existing.metadata,
                  ...service,
                  network: existing.metadata.network || service.network,
                  _serviceTypes: [...(existing.metadata._serviceTypes || ['airtime']), 'data']
                };
              } else {
                // New data-only account
                vasAccountMap.set(identifier, {
                  id: legacy.id * 1000 + 200 + idx,
                  type: 'data',
                  identifier: identifier,
                  label: service.label || `Data - ${service.network || ''}`,
                  isDefault: false,
                  metadata: {
                    network: service.network,
                    ...service,
                    _serviceTypes: ['data']
                  }
                });
              }
            }
          });
        }
        
        // Add all deduplicated VAS accounts
        accounts.push(...Array.from(vasAccountMap.values()));
      }
      
      if (legacy.utilityServices) {
        if (legacy.utilityServices.electricity && Array.isArray(legacy.utilityServices.electricity)) {
          legacy.utilityServices.electricity.forEach((service: any, idx: number) => {
            if (service.isActive === false) return;
            accounts.push({
              id: legacy.id * 1000 + 300 + idx,
              type: 'electricity',
              identifier: service.meterNumber,
              label: service.label || `Electricity - ${service.provider || ''}`,
              isDefault: false,
              metadata: {
                meterType: service.meterType,
                provider: service.provider,
                ...service
              }
            });
          });
        }
      }
      
      if (legacy.billerServices) {
        if (legacy.billerServices.accounts && Array.isArray(legacy.billerServices.accounts)) {
          legacy.billerServices.accounts.forEach((account: any, idx: number) => {
            accounts.push({
              id: legacy.id * 1000 + 400 + idx,
              type: 'biller',
              identifier: account.accountNumber,
              label: account.label || `${account.billerName} - ${account.accountNumber}`,
              isDefault: false,
              metadata: {
                billerName: account.billerName,
                category: account.category,
                ...account
              }
            });
          });
        }
      }
    }
    
    // If no accounts found, create a default one from legacy identifier
    // Skip creating a default for airtime/data if there are no active services (prevents showing stale entries)
    const legacyAccountType = (legacy.accountType as any) || undefined;
    const isAirtimeOrData = legacyAccountType === 'airtime' || legacyAccountType === 'data';
    if (accounts.length === 0 && legacy.identifier && !isAirtimeOrData) {
      accounts.push({
        id: legacy.id * 1000,
        type: (legacy.accountType as any) || 'mymoolah',
        identifier: legacy.identifier,
        label: legacy.bankName ? `${legacy.bankName} Account` : 'Default',
        isDefault: true,
        metadata: legacy.metadata || {}
      });
    }
    
    // Resolve the real phone number (MSISDN) for this beneficiary.
    // For bank-type beneficiaries the legacy.msisdn is a synthetic NON_MSI_ placeholder
    // and legacy.identifier is the bank ACCOUNT NUMBER — neither must be surfaced as a
    // phone. The real phone lives in paymentMethods.mymoolah.walletMsisdn (when a wallet
    // account is linked) or on the bank account's mobileNumber (PayShap RTP / FICA).
    const bankMobileFromJsonb = jsonbBankAccounts.find((ba) => ba && ba.mobileNumber)?.mobileNumber;
    const realMsisdn =
      legacy.paymentMethods?.mymoolah?.walletMsisdn ||
      legacy.paymentMethods?.mymoolah?.walletId ||
      (typeof legacy.msisdn === 'string' && !legacy.msisdn.startsWith('NON_MSI_') && isLikelySaMobile(legacy.msisdn)
        ? legacy.msisdn
        : null) ||
      bankMobileFromJsonb ||
      '';

    return {
      id: legacy.id,
      name: legacy.name,
      msisdn: realMsisdn,
      identifier: legacy.identifier,
      accountType: legacy.accountType,
      accounts,
      isFavorite: legacy.isFavorite || false,
      notes: legacy.notes,
      preferredPaymentMethod: legacy.preferredPaymentMethod,
      lastPaidAt: legacy.lastPaidAt,
      timesPaid: legacy.timesPaid || 0,
      createdAt: legacy.createdAt,
      updatedAt: legacy.updatedAt
    };
  }

  private mapToPaymentBeneficiary(
    beneficiary: UnifiedBeneficiary,
    idx: number = 0
  ): PaymentBeneficiary {
    // Filter to only payment accounts (mymoolah, bank, international_bank, mobile_money)
    const paymentAccounts = (beneficiary.accounts || []).filter(
      (account) =>
        account.type === 'mymoolah' ||
        account.type === 'bank' ||
        account.type === 'international_bank' ||
        account.type === 'mobile_money'
    );

    // Find primary account (default or first)
    const primaryAccount =
      paymentAccounts.find((account) => account.isDefault) ||
      paymentAccounts[0] ||
      null;

    const fallbackAccountType = (beneficiary as any)?.accountType as 'mymoolah' | 'bank' | undefined;
    const accountType =
      primaryAccount?.type === 'bank'
        ? 'bank'
        : (primaryAccount?.type === 'mymoolah'
            ? 'mymoolah'
            : (fallbackAccountType || 'mymoolah'));

    const identifier =
      accountType === 'bank'
        ? (primaryAccount?.identifier || (beneficiary as any)?.identifier || '')
        : beneficiary.msisdn ||
          primaryAccount?.identifier ||
          (beneficiary as any)?.identifier ||
          '';

    // For bank beneficiaries, find the MyMoolah account's phone as the real MSISDN;
    // fall back to a mobile number stored on the bank account (PayShap RTP / FICA payers).
    // CRITICAL: never surface the bank account number (primaryAccount.identifier /
    // beneficiary.identifier) or a non-phone msisdn as the contact's mobile.
    const mymoolahAccount = paymentAccounts.find(a => a.type === 'mymoolah');
    const bankAccountWithMobile = paymentAccounts.find(
      a => a.type === 'bank' && isLikelySaMobile(a.mobileNumber || a.metadata?.mobileNumber)
    );
    const realPhone =
      mymoolahAccount?.identifier ||
      mymoolahAccount?.metadata?.walletMsisdn ||
      bankAccountWithMobile?.mobileNumber ||
      bankAccountWithMobile?.metadata?.mobileNumber ||
      (isLikelySaMobile(beneficiary.msisdn) && beneficiary.msisdn !== primaryAccount?.identifier
        ? beneficiary.msisdn
        : null) ||
      '';

    return {
      id: String(beneficiary.id ?? idx),
      name: beneficiary.name,
      // Only ever a real phone number here — bank account numbers must NOT leak
      // into msisdn (they are exposed separately via identifier / accounts[]).
      msisdn: realPhone || '',
      identifier,
      accountType,
      bankName: primaryAccount?.metadata?.bankName,
      userId: (beneficiary as any)?.userId,
      createdAt: beneficiary.createdAt,
      updatedAt: beneficiary.updatedAt,
      timesPaid: beneficiary.timesPaid || 0,
      lastPaidAt: beneficiary.lastPaidAt,
      lastPaid: beneficiary.lastPaidAt ? new Date(beneficiary.lastPaidAt) : undefined,
      isFavorite: beneficiary.isFavorite,
      totalPaid: (beneficiary as any)?.metadata?.totalPaid ?? 0,
      paymentCount: beneficiary.timesPaid || 0,
      metadata: {
        preferredPaymentMethod: beneficiary.preferredPaymentMethod
      },
      // Include only payment accounts (mymoolah and bank) for account selector
      accounts: paymentAccounts.length > 0 ? paymentAccounts : undefined
    };
  }

  private normalizeMsisdn(value: string): string {
    if (!value) return '';
    const raw = String(value).trim();
    const digits = raw.replace(/\D/g, '');
    if (/^0[6-8]\d{8}$/.test(digits)) return `+27${digits.slice(1)}`;
    if (/^27[6-8]\d{8}$/.test(digits)) return `+${digits}`;
    if (raw.startsWith('+') && /^\+27[6-8]\d{8}$/.test(raw)) return raw;
    return raw; // return as-is; backend will validate and respond with error if invalid
  }
}

export const beneficiaryService = new BeneficiaryService();
